import type * as React from 'react';

import animationDataICRA from '@/assets/branding/icra/animationDataICRA.json';
import AskICRAChatIcon from '@/assets/branding/icra/ask-ICRA-button.svg?react';
import IcraDrive from '@/assets/branding/icra/ICRAdrive.svg?react';
import ICRAFullLogo from '@/assets/branding/icra/ICRALogo.svg?react';
import IcraNotes from '@/assets/branding/icra/ICRANotes.svg?react';
import AskICRAStarGradient from '@/assets/branding/icra/StarGradientICRA.svg?react';
import animationDataPWC from '@/assets/branding/pwc/animationDataPWC.json';
import AskPwcChatIcon from '@/assets/branding/pwc/ask-pwc-button.svg?react';
import PwcFullLogo from '@/assets/branding/pwc/pwcLogo.svg?react';
import AskPwcStarGradient from '@/assets/branding/pwc/StarGradientPWC.svg?react';
import NeedlLogo from '@/assets/needl-logo.svg?react';
import NeedlFullLogo from '@/assets/needl-logo-full.svg?react';
import animationDataNeedl from '@/features/ChatBotV2/components/Message/animationData.json';
import AskNeedlChatIcon from '@/icons/ask-needl-button.svg?react';
import AskNeedlStarGradient from '@/icons/StarGradient.svg?react';
import { isIcra, isPWC } from '@/utils/isEnv';

export const updateBrandingTitleAndFavicon = (
  title: string,
  faviconHref: string
) => {
  // We need to update the DOM elements after the document is ready
  const updateBranding = () => {
    // Update document title
    document.title = title;

    // Update favicon
    const favicon = document.getElementById('favicon') as HTMLLinkElement;

    if (favicon) {
      favicon.href = faviconHref;
    }
  };

  // Check if document is already complete, otherwise wait for it to be ready
  if (document.readyState === 'complete') {
    updateBranding();
  } else {
    window.addEventListener('DOMContentLoaded', updateBranding);
  }
};

export const getBrandingConfig = (): BrandingConfig => {
  if (isPWC()) {
    return {
      brandColor: '#fd5108',
      name: 'PwC',
      fullName: 'PwC',
      chatName: 'Client Intelligence and Insights Hub',
      storageName: 'PwC Storage',
      tabTitle: 'PwC',
      showShortLogo: false,
      faviconHref: '/pwc_favicon.ico',
      logos: {
        full: PwcFullLogo,
        icon: NeedlLogo,
        chatIcon: AskPwcChatIcon,
        starGradient: AskPwcStarGradient,
      },
      botMessageAnimationData: animationDataPWC,
      askNeedlGradientSecondary: '--emerald-500',
    };
  }

  if (isIcra()) {
    const IcraFooter: React.FC<{ fromSignUp: boolean }> = () => (
      <>Copyright © 2025 ICRA Limited. All Rights Reserved</>
    );

    return {
      brandColor: '#151c9d',
      name: 'ICRA',
      fullName: 'ICRA',
      chatName: 'Credit-IQ',
      storageName: 'ICRA Storage',
      tabTitle:
        "Credit-IQ | ICRA's Structured Intelligence for Credit Analysis",
      showShortLogo: false,
      faviconHref: '/icra-favicon.ico',
      logos: {
        full: ICRAFullLogo,
        icon: NeedlLogo,
        chatIcon: AskICRAChatIcon,
        starGradient: AskICRAStarGradient,
        needldrive: IcraDrive,
        needlnote: IcraNotes,
        needl_notebooks: IcraNotes,
      },
      botMessageAnimationData: animationDataICRA,
      askNeedlGradientSecondary: '--sunglow-400',
      loginWelcomeTitle: 'Welcome to ICRA Report Automation',
      loginFooterComponent: IcraFooter,
    };
  }

  return {
    brandColor: '#006D77',
    name: 'Needl',
    fullName: 'Needl.ai',
    chatName: 'AskNeedl',
    storageName: 'Needl Drive',
    tabTitle: 'needl.ai',
    showShortLogo: true,
    faviconHref: '/needl-favicon.png',
    logos: {
      full: NeedlFullLogo,
      icon: NeedlLogo,
      chatIcon: AskNeedlChatIcon,
      starGradient: AskNeedlStarGradient,
    },
    botMessageAnimationData: animationDataNeedl,
    askNeedlGradientSecondary: '--green-900',
  };
};

type BrandingConfig = {
  brandColor: string;
  name: string;
  fullName: string;
  chatName: string;
  storageName: string;
  tabTitle: string;
  showShortLogo: boolean;
  faviconHref: string;
  logos: {
    full: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    chatIcon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    starGradient: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    needldrive?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    needlnote?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
    needl_notebooks?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  };
  botMessageAnimationData: unknown;
  askNeedlGradientSecondary: string;
  poweredByBranding?: React.ReactNode;
  loginFooterComponent?: React.ComponentType<{ fromSignUp: boolean }>;
  loginWelcomeTitle?: string;
  authFooterMessage?: string;
};
